import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Form } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import { TextField, useMediaQuery } from "@mui/material";
import { AuthContext } from "../AuthContext";
import { CategoryContext } from "../CategoryContext";
//import CharacteristicInput from "../Components/CharacteristicInput/CharacteristicInput";
import ImageUpload from "../Components/ImageUpload/ImageUpload"; // Ensure this path is correct
import "./PostAd.css"; // Uporabimo enak CSS kot prej

export default function EditAd() {
    const navigate = useNavigate();
    const { slug, id } = useParams();
    const { user } = useContext(AuthContext);
    const categories = useContext(CategoryContext);

    const [title, setTitle] = useState("");
    const [price, setPrice] = useState("");
    const [state, setState] = useState(""); // This is item_condition
    const [negotiable, setNegotiable] = useState(false);
    const [description, setDescription] = useState("");
    const [images, setImages] = useState([]); // This state will be managed by ImageUpload
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [finalCategoryId, setFinalCategoryId] = useState(null);
    const [loading, setLoading] = useState(true); // Start loading true while fetching initial item data
    const [initialDataLoaded, setInitialDataLoaded] = useState(false); // To control rendering of ImageUpload
    const isMobile = useMediaQuery('(max-width:767px)');

    const [isImageUploading, setIsImageUploading] = useState(false);

    useEffect(() => {
        setLoading(true);
        async function fetchAd() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/oglas/${slug}/${id}`, {
                    // credentials: "include", // Only if your backend requires cookies for this GET
                    // headers: { "Content-Type": "application/json" }, // Not strictly needed for GET but fine
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || "Napaka pri nalaganju podatkov oglasa.");
                }

                const item = await response.json();

                // Basic authorization check on client-side (backend should enforce this too)
                if (user && item.user_id !== user.id) {
                    Swal.fire("Dostop zavrnjen", "Nimate dovoljenja za urejanje tega oglasa.", "error");
                    navigate('/'); // Or appropriate redirect
                    return;
                }

                setTitle(item.title || "");
                setPrice(item.price != null ? `${item.price}` : ""); // Keep as string with "€" or handle formatting in input
                setState(item.item_condition || "");
                setNegotiable(item.negotiable || false);
                setDescription(item.description || "");

                // MINIMAL CHANGE FOR IMAGES:
                // Prepare images for ImageUpload component. It expects an 'id' for each image.
                // We can use the database 'id' of the listing_image record or the 'image_url' if 'id' is not present.
                const formattedImages = item.images ? item.images.map(img => ({
                    id: img.id || img.image_url, // Use DB ID if available, fallback to URL for uniqueness
                    image_key: img.image_url,
                    is_primary: img.is_primary,
                    //file_name: img.file_name || '' // If your ImageUpload uses file_name
                })) : [];
                setImages(formattedImages);

                if (item.category_id && categories.length > 0) {
                    const categoryHierarchy = [];
                    let currentCategory = categories.find(cat => cat.id === item.category_id);
                    while (currentCategory) {
                        categoryHierarchy.unshift(currentCategory.id);
                        currentCategory = categories.find(cat => cat.id === currentCategory.parent_id);
                    }
                    setSelectedCategories(categoryHierarchy);
                    setFinalCategoryId(item.category_id);
                }
                setInitialDataLoaded(true); // Mark that data is loaded
            } catch (error) {
                console.error("Error fetching item for edit:", error);
                Swal.fire("Napaka!", error.message, "error");
                // Optionally navigate away if item fetch fails critically
                // navigate('/');
            } finally {
                setLoading(false);
            }
        }

        // Fetch item only if ID is present, categories are loaded (for category selector), and user is loaded (for auth check)
        if (id && categories.length > 0 && user) {
            fetchAd();
        } else if (id && categories.length > 0 && !user) {
            // If user is not loaded yet, you might want to wait or show a message.
            // For now, this will prevent fetchAd if user is null.
            // Consider if AuthContext provides a loading state for user.
        }

    }, [slug, id, categories, user, navigate]); // Added user and navigate to dependency array      

    const renderCategorySelectors = () => {
        // ... your existing renderCategorySelectors code ...
        // This function seems fine as is.
        const levels = [];
        let currentParentId = null;

        for (let level = 0; level <= selectedCategories.length; level++) {
            const categoriesAtLevel = categories.filter(cat => cat.parent_id === currentParentId);
            if (categoriesAtLevel.length === 0 && level < selectedCategories.length) {
                // This implies a selected category has no children, but we are trying to render one. Stop.
                // This might happen if categories were changed after selection.
                break;
            }
            if (categoriesAtLevel.length === 0 && level >= selectedCategories.length && finalCategoryId) break;


            const currentSelection = selectedCategories[level] || null;
            const options = categoriesAtLevel.map(cat => ({ value: cat.id, label: cat.name }));

            if (options.length === 0 && level < selectedCategories.length) continue;

            levels.push(
                <div key={level} className="mb-3" style={{ position: 'relative', zIndex: 1000 - level }}> {/* Adjusted zIndex */}
                    <Select
                        className="custom-react-select"
                        classNamePrefix="custom-react-select"
                        value={options.find(option => option.value === currentSelection) || null}
                        onChange={(selectedOption) => {
                            const newSelected = [...selectedCategories.slice(0, level)];
                            if (selectedOption) {
                                newSelected.push(selectedOption.value);
                            }
                            setSelectedCategories(newSelected);

                            const hasSub = categories.some(cat => cat.parent_id === (selectedOption ? selectedOption.value : null));
                            setFinalCategoryId(hasSub ? null : (selectedOption ? selectedOption.value : (level > 0 ? selectedCategories[level - 1] : null)));
                        }}
                        options={options}
                        isClearable
                        placeholder={level === 0 ? "Izberi kategorijo" : "Izberi podkategorijo"}
                        isSearchable={false}
                    />
                </div>
            );

            if (!selectedCategories[level] && level < selectedCategories.length) break;
            currentParentId = selectedCategories[level];
        }
        return levels;
    };

    const handlePriceChange = (e) => {
        // ... your existing handlePriceChange code ...
        // This function seems fine as is.
        let value = e.target.value;
        value = value.replace(/[^\d.,]/g, ""); // Keep only digits, dot, or comma
        // Ensure only one decimal separator (prefer dot, but allow comma for typing)
        const match = value.match(/([.,])/g);
        if (match && match.length > 1) {
            // If multiple separators, keep only the first one encountered
            const firstSeparatorIndex = value.indexOf(match[0]);
            value = value.substring(0, firstSeparatorIndex + 1) + value.substring(firstSeparatorIndex + 1).replace(/[.,]/g, '');
        }

        if (value === "") {
            setPrice("");
            return;
        }
        // For display, format with "€", but store without for easier parsing later
        // For now, keep it as the user types, formatting can be complex here
        setPrice(value); // Store the potentially unformatted value
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!finalCategoryId) {
            Swal.fire("Napaka!", "Izberite veljavno kategorijo.", "error");
            setLoading(false);
            return;
        }
        if (!user) { // Should be caught by useEffect but as a safeguard
            Swal.fire("Napaka!", "Za urejanje oglasa morate biti prijavljeni.", "error");
            setLoading(false);
            return;
        }

        // Prepare price: remove " €" and convert comma to dot for float parsing
        const numericPrice = parseFloat(String(price).replace(" €", "").replace(",", ".")) || 0;

        const adDataImages = images
            .filter(img => {
                // Dodatno logiranje znotraj filtra
                if (!img.image_key) {
                }
                return img.image_key;
            })
            .map(img => ({
                image_key: img.image_key,
                is_primary: !!img.is_primary,
            }));

        const adData = {
            title,
            description,
            item_condition: state,
            price: numericPrice,
            category_id: finalCategoryId,
            // user_id: user.id, // Backend should infer user_id from session/token for security
            negotiable,
            // MINIMAL CHANGE FOR IMAGES:
            // Send the current state of images. ImageUpload component ensures `url` and `is_primary` are set.
            images: adDataImages,
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/oglas/${slug}/${id}`, {
                method: "PUT",
                credentials: "include", // If using httpOnly cookies for auth
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(adData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Napaka pri shranjevanju sprememb oglasa.");
            }

            // const result = await response.json(); // Contains { message, listing }

            Swal.fire({
                title: "Sprejeto!",
                text: "Oglas je bil uspešno posodobljen. Slike se obdelujejo v ozadju in bodo kmalu vidne.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            }).then(() => {
                navigate(`/oglas/${slug}/${id}`);
            });
        } catch (err) {
            console.error("Error updating item:", err);
            Swal.fire("Napaka!", err.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const conditionOptions = [
        { value: 'Novo', label: 'Novo' },
        { value: 'Rabljeno', label: 'Rabljeno' },
        { value: 'Poškodovano', label: 'Poškodovano' }
    ];

    // Conditional rendering while data is loading
    if (loading && !initialDataLoaded) {
        return (
            <div className="post_item_bg" id="post_item_loading">
                <Container className="text-center py-5">
                    <p>Nalaganje podatkov oglasa...</p>
                    {/* You could add a spinner here */}
                </Container>
            </div>
        );
    }


    return (
        <div className="post_item_bg" id="post_item">
            <Container className="post_item_container">
                <div className="post_item_div">
                    <h2 className="post_item_title mb-4">Uredi oglas</h2>
                    <form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Naziv oglasa</Form.Label>
                            <Form.Control type="text" placeholder="Naziv oglasa" value={title} onChange={(e) => setTitle(e.target.value)} required />
                        </Form.Group>

                        <Row className="cena mb-3">
                            <Col md={7} style={{ paddingLeft: '0' }}> {/* Adjusted padding to align with FormControlLabel */}
                                <Form.Group>
                                    <Form.Label>Cena</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Cena"
                                        value={price.endsWith(' €') ? price : (price ? `${price} €` : '')} // Display with €
                                        onChange={handlePriceChange}
                                        onBlur={() => { // On blur, ensure it's just the number or empty
                                            const val = String(price).replace(" €", "").replace(",", ".");
                                            setPrice(val);
                                        }}
                                        required={!negotiable} // Price is required if not negotiable
                                    />
                                </Form.Group>
                            </Col>
                            {/*<Col md={5} className="d-flex align-items-end pb-1">
                                <FormControlLabel
                                    control={<Checkbox checked={negotiable} onChange={(e) => setNegotiable(e.target.checked)} />}
                                    label="Po dogovoru"
                                />
                            </Col>*/}
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Stanje</Form.Label>
                            <Select
                                className="custom-react-select"
                                classNamePrefix="custom-react-select"
                                value={conditionOptions.find(option => option.value === state)}
                                onChange={(selectedOption) => setState(selectedOption ? selectedOption.value : '')}
                                options={conditionOptions}
                                placeholder="Izberite stanje"
                                isClearable
                                isSearchable={false}
                                required
                            />
                        </Form.Group>

                        <h5 className="category_title mt-4">Kategorija izdelka</h5>
                        {initialDataLoaded && renderCategorySelectors()} {/* Render only after categories might be set */}

                        <TextField
                            className="mt-3 mb-3" // Added some margin
                            fullWidth
                            label="Opis izdelka"
                            multiline
                            minRows={isMobile ? 10 : 3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            required
                            margin="normal"
                        />
                        <h5 className="uploitem_picture_title mt-4">Slike izdelka</h5>
                        {/* Render ImageUpload only after initial data (including images) is loaded */}
                        {initialDataLoaded && <ImageUpload images={images} setImages={setImages} setIsUploading={setIsImageUploading} />}

                        <button
                            type="submit"
                            className="btn btn-primary mt-4 w-100 objavi_oglas_btn"
                            disabled={loading || isImageUploading} // Onemogoči med shranjevanjem ALI nalaganjem slik
                        >
                            {isImageUploading ? "Nalaganje slik..." : (loading ? "Shranjevanje..." : "Shrani spremembe")}
                        </button>
                    </form>
                </div>
            </Container>
        </div>
    );
}